import React, { useState, useEffect } from 'react'
import {
  Modal,
  Button,
  Form,
  Input,
  Select,
  Icon,
  message,
  Tooltip,
  Result,
  Spin,
  Alert,
  Steps,
  Row,
  Col
} from 'antd'
import {
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { api } from '../../../../../../services'
import actions from '../../../../../../store/actions'
import history from '../../../../../../history'
import ContentChoiceStep from '../../../../../../components/ContentChoiceSelection'

import './styles.scss'

const { Step } = Steps

const AiMazeGeneration = ({
  onClose,
  onMazeGenerated,
  generatedStatus,
  form
}) => {
  const user = useSelector(state => state.user)
  const maze = useSelector(state => state.mazeBuilder)
  const [status, setStatus] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [formValues, setFormValues] = useState(null)

  const dispatch = useDispatch()
  const { t } = useTranslation()

  const generationStatus = {
    in_progress: 'in_progress',
    completed: 'completed',
    error: 'error'
  }

  const formFields = {
    end_goal: {
      value: t('v5:end_goal'),
      description: t('v5:end_goal_description')
    },
    problem_statement: {
      value: t('v5:problem_statement'),
      description: t('v5:problem_statement_description')
    },
    learner_role: {
      value: t('v5:learner_role'),
      description: t('v5:learner_role_description')
    },
    depth: {
      value: t('v5:depth'),
      description: t('v5:depth_description')
    },
    no_of_options: {
      value: t('v5:no_of_options'),
      description: t('v5:no_of_options_description')
    }
  }

  useEffect(() => {
    if (generatedStatus) setStatus(handleGenerationStatus(generatedStatus))

    if (generatedStatus === generationStatus.in_progress) {
      checkMazeStatus()
    }
  }, [generatedStatus])

  const checkMazeStatus = () => {
    setStatus(generationStatus.in_progress)
    api.maze
      .getMazeById(maze.mazeId)
      .then(res => {
        const mazeObj = res.data.data
        if (mazeObj && mazeObj.generated_status === 'in_progress') {
          _.delay(checkMazeStatus, 20000)
        } else if (mazeObj && mazeObj.generated_status === 'completed') {
          onMazeGenerated()
          setStatus('')
        } else if (mazeObj && mazeObj.generated_status === 'error') {
          setStatus(generationStatus.error)
        }
      })
      .catch(error => {
        console.error('Error checking maze status:', error)
        setStatus(generationStatus.error)
      })
  }

  const handleGenerationStatus = input => {
    const { in_progress, completed, error } = generationStatus
    const newStatus =
      input === 'in_progress'
        ? in_progress
        : input === 'completed'
          ? completed
          : error

    return newStatus
  }

  const handleModalClose = () => {
    const { mazeId } = maze

    if (status === generationStatus.in_progress || isGenerating) {
      dispatch(actions.maze.getMazeById(mazeId))
      history.push(`/maze/${mazeId}/edit`)
    } else {
      onClose()
    }
  }

  const handleNext = (e) => {
    e.preventDefault()
    form.validateFields((errors, values) => {
      if (!errors) {
        setFormValues(values)
        setCurrentStep(1)
      }
    })
  }

  const handleBack = () => {
    setCurrentStep(0)
  }

  const handleFinalSubmit = async (files) => {
    setIsGenerating(true)

    const {
      end_goal,
      problem_statement,
      learner_role,
      depth,
      number_of_options
    } = formValues


    // Determine if files are uploads or library documents
    // ContentChoiceSelection returns array of objects.
    // If uploaded: usually has 'path' or 'url'.
    // If manual: has 'id'.
    // If auto: empty array.

    let payload = {
      end_goal,
      problem_statement,
      learner_role,
      depth,
      number_of_options,
      title: maze.mazeTitle,
      programme_id: maze.mazeId,
      user_id: user?.info?.id,
      email: user?.info?.email,
      username: user?.info?.username,
      org_id: user?.info?.active_organization_id
    }

    if (files && files.length > 0) {
      // Check if it's a library document (has id) or uploaded file (has path/url but we need to check how ContentChoiceSelection returns it)
      // Based on ContentChoiceSetup.jsx:
      // Manual: returns selectedFiles (objects with id)
      // Upload: returns newFiles (response from upload action)

      // We can check for 'id' to distinguish.
      const isLibraryDoc = files.every(f => f.id && !f.path && !f.url) // Simple check, might need refinement based on actual object structure

      if (isLibraryDoc) {
        payload.document_ids = files.map(doc => doc.id)
      } else {
        // Assume uploaded files
        // The API expects 'file_list' for uploaded files paths
        // We need to extract paths from the file objects
        const filePaths = files.map(f => f.path || f.url || f.name).filter(Boolean)
        payload.file_list = filePaths
        payload.org_file = true // Based on previous implementation for uploads
      }
    }

    try {
      const res = await api.maze.generateMazeByAi(payload)
      if (res.data) {
        const status = res.data.status
        if (status === 'success') {
          checkMazeStatus()
        }
      }
    } catch (err) {
      console.log(err)
      message.error('An error occurred while generating the maze.')
      setStatus(generationStatus.error)
    } finally {
      setIsGenerating(false)
    }
  }

  const { getFieldDecorator } = form

  const GenerationFormView = () => {
    return (
      <Form layout="vertical" className="ai-maze-form-modal__body">
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={
                <span className="ai-maze-form-label">
                  <span>{formFields.end_goal.value}</span>
                  <Tooltip title={formFields.end_goal.description}>
                    <Icon type="info-circle" />
                  </Tooltip>
                </span>
              }
              colon={false}
              required={false}
            >
              {getFieldDecorator('end_goal', {
                rules: [{ required: true, message: 'Please input end goal' }],
                initialValue: formValues ? formValues.end_goal : undefined
              })(<Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} />)}
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label={
                <span className="ai-maze-form-label">
                  <span>{formFields.problem_statement.value}</span>
                  <Tooltip title={formFields.problem_statement.description}>
                    <Icon type="info-circle" />
                  </Tooltip>
                </span>
              }
              colon={false}
              required={false}
            >
              {getFieldDecorator('problem_statement', {
                rules: [
                  { required: true, message: 'Please input problem statement' }
                ],
                initialValue: formValues
                  ? formValues.problem_statement
                  : undefined
              })(<Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} />)}
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label={
                <span className="ai-maze-form-label">
                  <span>{formFields.learner_role.value}</span>
                  <Tooltip title={formFields.learner_role.description}>
                    <Icon type="info-circle" />
                  </Tooltip>
                </span>
              }
              colon={false}
              required={false}
            >
              {getFieldDecorator('learner_role', {
                rules: [{ required: true, message: 'Please input learner role' }],
                initialValue: formValues ? formValues.learner_role : undefined
              })(<Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />)}
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <span className="ai-maze-form-label">
                  <span>{formFields.depth.value}</span>
                  <Tooltip title={formFields.depth.description}>
                    <Icon type="info-circle" />
                  </Tooltip>
                </span>
              }
              colon={false}
              required={false}
            >
              {getFieldDecorator('depth', {
                rules: [{ required: true, message: 'Please select depth' }],
                initialValue: formValues ? formValues.depth : 2
              })(
                <Select>
                  {[2, 3, 4, 5, 6, 7, 8].map(value => (
                    <Select.Option key={value} value={value}>
                      {value}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={
                <span className="ai-maze-form-label">
                  <span>{formFields.no_of_options.value}</span>
                  <Tooltip title={formFields.no_of_options.description}>
                    <Icon type="info-circle" />
                  </Tooltip>
                </span>
              }
              colon={false}
              required={false}
            >
              {getFieldDecorator('number_of_options', {
                rules: [
                  { required: true, message: 'Please select number of options' }
                ],
                initialValue: formValues ? formValues.number_of_options : 2
              })(
                <Select>
                  {[2, 3, 4].map(value => (
                    <Select.Option key={value} value={value}>
                      {value}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
        </Row>

        {status && status === generationStatus.error && (
          <Alert
            message="Error"
            description={t('v4:failed_maze_generate')}
            type="error"
            closable
            showIcon
            icon={<CloseCircleOutlined />}
            style={{ marginBottom: 24 }}
          />
        )}

        <Row>
          <Col span={24} style={{ textAlign: 'right', marginTop: 16 }}>
            <Form.Item>
              <Button
                shape="round"
                size="large"
                type="primary"
                onClick={handleNext}
              >
                {t('buttons:next')}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    )
  }

  const GenerationStatusView = () => {
    const { in_progress, completed, error } = generationStatus

    // Customize loading icon for Spin
    const loadingIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />

    if (status === in_progress) {
      return (
        <Result
          icon={<Spin indicator={loadingIcon} />}
          title={t('v4:generate_metamaze')}
          subTitle={t('v4:metamaze_being_generated')}
          extra={
            <Button
              type="primary"
              key="console"
              onClick={() => history.push('/')}
            >
              Go To Training
            </Button>
          }
        />
      )
    }

    if (status === completed) {
      return (
        <Result
          status="success"
          title={t('v4:metamaze_generate_successfully')}
          subTitle={t('v4:metamaze_with_proceed')}
          icon={<CheckCircleOutlined />}
        />
      )
    }

    return null
  }

  console.log('maze', maze)

  return (
    <Modal
      visible
      centered
      footer={null}
      closable={false}
      className="ai-maze-form-modal"
      width={800}
    >
      <div className="ai-maze-form-modal__header">
        <p>{t('v4:ai_metamaze_generate')}</p>
        <Icon type="close" onClick={handleModalClose} />
      </div>

      {status && status !== generationStatus.error ? (
        GenerationStatusView()
      ) : (
        <>
          <div style={{ padding: '0 24px' }}>
            <Steps current={currentStep} style={{ marginBottom: 20 }}>
              <Step title={t('v5:maze_details') || 'Maze Details'} />
              <Step title={t('v4:content_selection')} />
            </Steps>
          </div>

          {currentStep === 0 && GenerationFormView()}
          {currentStep === 1 && (
            <ContentChoiceStep
              onCancel={handleBack}
              handleBack={handleBack}
              handleFinalSubmit={handleFinalSubmit}
              isSubmitting={isGenerating}
            />
          )}
        </>
      )}
    </Modal>
  )
}

// Wrap AiMazeGeneration component with Form.create() to access form methods via props
const WrappedAiMazeGeneration = Form.create({ name: 'ai_maze_generation' })(
  AiMazeGeneration
)

export default WrappedAiMazeGeneration
